import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Activity, Search, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import useAdminAuth, { adminAuthHeader } from "@/hooks/useAdminAuth";
import AdminGate from "@/components/admin/AdminGate";
import AdminHeader from "@/components/admin/AdminHeader";
import DedupPanel from "@/components/admin/DedupPanel";
import LeadsTable from "@/components/admin/LeadsTable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLeadsPage() {
  const { authed, verifying, error: authError, busy, login, logout, expireSession } = useAdminAuth();
  const [leads, setLeads] = useState([]);
  const [dedup, setDedup] = useState({ pairs: [], server: [], pixel: [] });
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("leads");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, dedupRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers: adminAuthHeader() }),
        axios.get(`${API}/admin/capi-events?limit=100`, { headers: adminAuthHeader() })
          .catch(() => ({ data: { pairs: [], server: [], pixel: [] } })),
      ]);
      setLeads(leadsRes.data);
      setDedup(dedupRes.data || { pairs: [], server: [], pixel: [] });
      setError("");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore nel caricamento";
      setError(typeof detail === "string" ? detail : "Errore di rete");
      if (e?.response?.status === 401) expireSession();
    } finally { setLoading(false); }
  }, [expireSession]);

  useEffect(() => { if (authed) loadLeads(); }, [authed, loadLeads]);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter((l) =>
      l.name?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.phone?.toLowerCase().includes(q) ||
      l.message?.toLowerCase().includes(q)
    );
  }, [leads, query]);

  if (verifying) return null;
  if (!authed) {
    return <AdminGate
      title="Area riservata"
      subtitle="Accedi con la password admin per consultare le richieste di informazioni."
      onSubmit={login} error={authError} busy={busy}
    />;
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--np-tertiary)" }}>
      <AdminHeader
        title="Admin · Lead Inbox" subtitle="Neuroscienza Pratica — Alessandro Fulgheri"
        icon={<Users size={20} color="#ffffff" />}
        navLinks={[{ label: "Funnel Dashboard", href: "/admin/dashboard", testid: "nav-admin-dashboard" }]}
        onRefresh={loadLeads} refreshing={loading} onLogout={logout}
      />

      <main data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-7 flex items-center gap-2 border-b border-[#e3eaf2]">
          <button type="button" onClick={() => setTab("leads")} data-testid="admin-tab-leads"
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${tab === "leads" ? "border-[var(--np-secondary)]" : "border-transparent hover:border-[#dbe3ec]"}`}
            style={{ color: tab === "leads" ? "var(--np-secondary)" : "var(--np-muted)" }}>
            Lead ({leads.length})
          </button>
          <button type="button" onClick={() => setTab("dedup")} data-testid="admin-tab-dedup"
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition inline-flex items-center gap-1.5 ${tab === "dedup" ? "border-[var(--np-secondary)]" : "border-transparent hover:border-[#dbe3ec]"}`}
            style={{ color: tab === "dedup" ? "var(--np-secondary)" : "var(--np-muted)" }}>
            <Activity size={14} /> Eventi Meta · Dedup ({dedup.pairs?.length || 0})
          </button>
        </div>

        {tab === "dedup" ? <DedupPanel dedup={dedup} /> : (
          <>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-7">
              <div>
                <h2 className="text-2xl md:text-3xl tracking-tight" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>Richieste di informazioni</h2>
                <p data-testid="admin-leads-count" className="text-sm mt-1" style={{ color: "var(--np-muted)" }}>
                  {leads.length} totali · {filtered.length} visualizzate
                </p>
              </div>
              <div className="relative w-full md:w-80">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--np-muted)" }} />
                <Input data-testid="admin-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca per nome, email, telefono…"
                  className="pl-9 h-11 rounded-lg bg-white border-[#dbe3ec] focus-visible:ring-[var(--np-secondary)]" />
              </div>
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4">{error}</p>}
            <div className="bg-white rounded-2xl border border-[#e3eaf2] overflow-hidden shadow-[0_15px_50px_-30px_rgba(26,42,58,0.35)]">
              <LeadsTable leads={filtered} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
